import { useContext, useMemo } from 'react';
import { Column } from 'react-table';
import { Button, Dropdown, MenuProps, Modal, Select, message } from 'antd';
import * as Icons from '@ant-design/icons';

import { StopPropagation } from '@shared/components/stop-propagation';
import { CheckCell } from '@shared/components/cells';
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns';
import { NextTableCheckboxFilter } from '@shared/modules/next-table/components/filters';
import { useBackgroundNavigate } from '@shared/hooks';
import { useAuth } from '@modules/me';
import { useStatusesByModelId } from '@modules/statuses';

import { BranchPartnersService } from '../../services';
import { IBranchPartner } from '../../interfaces';
import { BranchPartnersTableContext } from '../../context';
import { branchPartnerQueryKeys } from '../../utils';

export const useBranchPartnersTableColumns = (): Column<IBranchPartner>[] => {
  const { handleFetch } = useContext(BranchPartnersTableContext);
  const { can } = useAuth();
  const navigate = useBackgroundNavigate();
  const states = useStatusesByModelId(44);

  const actionsColumn = useMemo<Column<IBranchPartner>>(
    () => ({
      ...nextTableColumns.actions,
      Cell: ({ row: { original } }: any) => {
        const items: MenuProps['items'] = [
          ...(can('company_edit')
            ? [
                {
                  key: 'edit',
                  label: 'Düzəliş et',
                  icon: <Icons.EditOutlined />,
                  onClick: () =>
                    navigate(`/branch-partners/${original.id}/update`, {
                      withBackground: true,
                    }),
                } as NonNullable<MenuProps['items']>[number],
              ]
            : []),
          ...(can('company_edit') && can('company_delete') ? [{ type: 'divider' } as NonNullable<MenuProps['items']>[number]] : []),
          ...(can('company_delete')
            ? [
                {
                  key: 'delete',
                  label: 'Sil',
                  icon: <Icons.DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Diqqət',
                      content: 'Partnyor şirkəti silməyə əminsinizmi?',
                      okText: 'Sil',
                      cancelText: 'Ləğv et',
                      okType: 'danger',
                      onOk: async () => {
                        const result = await BranchPartnersService.delete(original.id);
                        if (result.status === 200) {
                          message.success('Partnyor şirkət silindi');
                          handleFetch();
                        } else {
                          message.error(result.data as string);
                        }
                      },
                    });
                  },
                } as NonNullable<MenuProps['items']>[number],
              ]
            : []),
        ];

        return (
          <StopPropagation>
            <Dropdown menu={{ items }} trigger={['hover']}>
              <Button icon={<Icons.MoreOutlined />} size="small" />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [navigate, handleFetch, can],
  );

  const baseColumns = useMemo<Column<IBranchPartner>[]>(
    () => [
      {
        ...nextTableColumns.small,
        accessor: (row) => row.id,
        id: branchPartnerQueryKeys.id,
        Header: 'Kod',
      },
      {
        ...nextTableColumns.large,
        accessor: (row) => row.name,
        id: branchPartnerQueryKeys.name,
        Header: 'Şirkət adı',
      },
      {
        ...nextTableColumns.small,
        accessor: (row) => row.isOwner,
        id: branchPartnerQueryKeys.isOwner,
        Header: 'Sahib',
        Cell: CheckCell,
        Filter: NextTableCheckboxFilter,
      },
      {
        accessor: (row) => row.state?.name,
        id: branchPartnerQueryKeys.state.id,
        Header: 'Status',
        Filter: ({ column: { filterValue, setFilter } }: any) => {
          return (
            <Select allowClear={true} style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
              {states.data?.map((state) => (
                <Select.Option key={state.id} value={state.id.toString()}>
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          );
        },
      },
      {
        accessor: (row) => row.contact,
        id: branchPartnerQueryKeys.contact,
        Header: 'Əlaqə nömrə',
      },
      {
        ...nextTableColumns.date,
        accessor: (row) => row.createdAt,
        id: branchPartnerQueryKeys.createdAt,
        Header: 'Yaradılıb',
      },
    ],
    [states.data],
  );

  return useMemo(() => [actionsColumn, ...baseColumns], [actionsColumn, baseColumns]);
};
